/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { defaultLanguage, type EditorLanguage } from '../../components/LanguageSelection/languages';
import type { EditorPlugins } from '../../types/EditorPlugins';
import { groupByInstallPackage } from '../groupByInstallPackage/groupByInstallPackage';

export function getTranslationImports( editorLanguage: EditorLanguage, hasPremiumPlugins: boolean ): string {
	const shouldLoadTranslations = areTranslationsNeeded( editorLanguage );
	const shouldLoadPremiumTranslations = hasPremiumPlugins && shouldLoadTranslations;

	const imports: Array<string> = [
		shouldLoadTranslations ?
			`import translations from 'ckeditor5/translations/${ editorLanguage }.js';` :
			'',
		shouldLoadPremiumTranslations ?
			`import premiumFeaturesTranslations from 'ckeditor5-premium-features/translations/${ editorLanguage }.js';` :
			''
	];

	return imports.filter( Boolean ).join( '\n' );
}

export function getTranslationModuleNames( editorLanguage: EditorLanguage, editorPlugins: EditorPlugins ): Array<string> {
	const { 'ckeditor5-premium-features': ckeditor5PremiumFeatures } = groupByInstallPackage( editorPlugins );

	const hasPremiumFeatures = ckeditor5PremiumFeatures.length > 0;
	const shouldLoadTranslations = areTranslationsNeeded( editorLanguage );
	const shouldLoadPremiumTranslations = hasPremiumFeatures && shouldLoadTranslations;

	return [
		shouldLoadTranslations ? `ckeditor5/translations/${ editorLanguage }.js` : '',
		shouldLoadPremiumTranslations ? `ckeditor5-premium-features/translations/${ editorLanguage }.js` : ''
	].filter( Boolean );
}

export function getTranslationVariables( editorLanguage: EditorLanguage, editorPlugins: EditorPlugins ): string {
	const groupedPlugins = groupByInstallPackage( editorPlugins );
	const hasPremiumPlugins = groupedPlugins[ 'ckeditor5-premium-features' ].length > 0;

	return [ ...getTranslationImports( editorLanguage, hasPremiumPlugins ).matchAll( /(?:import )(\w+)/g ) ]
		.map( ( [ , variableName ] ) => variableName )
		.join( ', ' );
}

export function areTranslationsNeeded( editorLanguage: EditorLanguage ): boolean {
	return editorLanguage !== defaultLanguage;
}
